import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { isEqual } from "lodash";
import { CustomSelect, FilterOptions, TermFilter } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/activity-report/ActivityReportAction";
import { useActivityReportUIContext } from "../ActivityReportUIContext";

const prepareFilter = (queryParams, values) => {
  let { term, date, dateEnd, sectionId, studentId, teacherId, activityTypeId, title, explanation, attachmentName } = values;
  const newQueryParams = { ...queryParams };
  const filter = { date, dateEnd, sectionId, studentId, teacherId, activityTypeId, title, explanation, attachmentName };
  newQueryParams.filter = filter;
  newQueryParams.term = term;
  return newQueryParams;
};

export function ActivityReportFilter() {
  // Activity Report UI Context
  const activityReportUIContext = useActivityReportUIContext();
  const activityReportUIProps = useMemo(() => {
    return {
      queryParams: activityReportUIContext.queryParams,
      setQueryParams: activityReportUIContext.setQueryParams
    };
  }, [activityReportUIContext]);

  const dispatch = useDispatch();
  const { currentActiveTerm, sections, students, teachers, activityTypes } = useSelector(
    (state) => ({
      currentActiveTerm: state.auth.user.currentActiveTerm,
      sections: state.activityReport.sections,
      students: state.activityReport.students,
      teachers: state.activityReport.teachers,
      activityTypes: state.activityReport.activityTypes
    }),
    shallowEqual
  );

  useEffect(() => {
    dispatch(actions.getList());
  }, [dispatch]);

  const applyFilter = (values) => {
    const newQueryParams = prepareFilter(activityReportUIProps.queryParams, values);
    if (!isEqual(newQueryParams, activityReportUIProps.queryParams)) {
      newQueryParams.pageNumber = 1;
      activityReportUIProps.setQueryParams(newQueryParams);
    }
  };

  const sectionOptions = FilterOptions.concat(sections);
  const studentOptions = FilterOptions.concat(students);
  const teacherOptions = FilterOptions.concat(teachers);
  const activityTypeOptions = FilterOptions.concat(activityTypes);

  return (
    <Formik
      initialValues={{
        term: currentActiveTerm,
        title: "",
        explanation: "",
        date: "",
        dateEnd: "",
        attachmentName: ""
      }}
      onSubmit={(values) => {
        applyFilter(values);
      }}
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit} className="form form-label-right">
          <TermFilter
            handleSubmit={handleSubmit}
            handleBlur={handleBlur}
            handleChange={handleChange}
            onChange={e => {
              if (values.sectionId) dispatch(actions.getStudents(e.target.value, values.sectionId));
              dispatch(actions.getTeachers(e.target.value));
            }}
            values={values}
          />
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Tarih
            </label>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="date"
                placeholder="Başlangıç"
                onBlur={handleBlur}
                value={values.date}
                onChange={(e) => {
                  setFieldValue("date", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="dateEnd"
                placeholder="Bitiş"
                onBlur={handleBlur}
                value={values.dateEnd}
                onChange={(e) => {
                  setFieldValue("dateEnd", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Sınıf
            </label>
            <div className="col-lg-4">
              <CustomSelect 
                options={sectionOptions}
                value={values.sectionId}
                placeholder="Sınıf"
                onBlur={handleBlur}
                onChange={e => {
                  if (e.value) dispatch(actions.getStudents(values.term, e.value));
                  dispatch(actions.cleanStudentsState());
                  setFieldValue("sectionId", e.value);
                  setFieldValue("studentId", undefined);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Öğrenci
            </label>
            <div className="col-lg-4">
              <CustomSelect 
                options={studentOptions}
                value={values.studentId}
                placeholder="Öğrenci"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("studentId", e.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-lg-2">
              <CustomSelect 
                options={teacherOptions}
                value={values.teacherId}
                placeholder="Öğretmen"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("teacherId", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Öğretmen</b>
              </small>
            </div>
            <div className="col-lg-2">
              <CustomSelect 
                options={activityTypeOptions}
                value={values.activityTypeId}
                placeholder="Faaliyet Türü"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("activityTypeId", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Faaliyet Türü</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="text"
                className="form-control"
                name="title"
                placeholder="Başlık"
                onBlur={handleBlur}
                value={values.title}
                onChange={(e) => {
                  setFieldValue("title", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Başlık</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="text"
                className="form-control"
                name="explanation"
                placeholder="Açıklama"
                onBlur={handleBlur}
                value={values.explanation}
                onChange={(e) => {
                  setFieldValue("explanation", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Açıklama</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="text"
                className="form-control"
                name="attachmentName"
                placeholder="Ek"
                onBlur={handleBlur}
                value={values.attachmentName}
                onChange={(e) => {
                  setFieldValue("attachmentName", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Ek</b>
              </small>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}